/**
 * FeatureTour — Generic step-by-step onboarding overlay.
 *
 * Adapted from ``WelcomeTour`` (dashboard first-visit walkthrough) but
 * driven by a ``steps`` prop so we can reuse it across different pages
 * (pricing, calendar, guestbook, etc.).
 *
 * Usage:
 *   <FeatureTour
 *     storageKey="tour_pricing_v1"
 *     steps={PRICING_STEPS}
 *     forceOpen={showTutorial}          // optional: bypass "seen once" gate
 *     onClose={() => setShowTutorial(false)}
 *   />
 *
 * Each step supports:
 *   - `selector`: CSS selector of the element to spotlight (optional).
 *                 If missing, the card is centered on desktop and shown
 *                 as a bottom-sheet on mobile.
 *   - `icon`:     lucide-react icon component.
 *   - `color`:    HEX accent color for the icon halo + primary button.
 *   - `title`, `text`: strings.
 *   - `bullets`:  optional list of short strings shown as ticked items.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, X, Check } from "lucide-react";

const PAD = 8;

export const FeatureTour = ({ storageKey, steps, forceOpen = false, onClose }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const scrollRootRef = useRef(null);

  // First-visit auto-open (unless storageKey is null — then only forceOpen matters)
  useEffect(() => {
    if (forceOpen) {
      setStep(0);
      setOpen(true);
      return;
    }
    if (!storageKey) return;
    if (!localStorage.getItem(storageKey)) {
      const t = setTimeout(() => { setStep(0); setOpen(true); }, 800);
      return () => clearTimeout(t);
    }
  }, [storageKey, forceOpen]);

  // Prevent body scroll while open (feels much nicer on mobile)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const measure = useCallback(() => {
    const s = steps[step];
    const sel = s?.selector;
    const el = sel ? document.querySelector(sel) : null;
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    }, 320);
  }, [step, steps]);

  useEffect(() => {
    if (!open) return;
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, measure]);

  const close = () => {
    if (storageKey) localStorage.setItem(storageKey, "1");
    setOpen(false);
    onClose?.();
  };
  const next = () => (step < steps.length - 1 ? setStep(step + 1) : close());
  const prev = () => step > 0 && setStep(step - 1);

  if (!open || !steps?.length) return null;

  const s = steps[step];
  const Icon = s.icon;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  // On mobile we ALWAYS show as bottom sheet — anchoring next to tiny buttons
  // makes the card fall off-screen most of the time.
  const anchored = rect && !isMobile;

  let cardStyle;
  if (anchored) {
    const below = rect.top + rect.height + 260 < window.innerHeight;
    cardStyle = {
      top: below ? rect.top + rect.height + 14 : undefined,
      bottom: below ? undefined : window.innerHeight - rect.top + 14,
      left: Math.max(16, Math.min(rect.left + rect.width / 2 - 200, window.innerWidth - 416))
    };
  }

  const card = (
    <motion.div
      key={step}
      initial={{ opacity: 0, y: 24, scale: 0.96, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -12, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 160, damping: 20 }}
      className={
        anchored
          ? "fixed w-[400px] bg-white rounded-3xl p-5 shadow-2xl pointer-events-auto"
          : "w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl pointer-events-auto"
      }
      style={anchored ? cardStyle : undefined}
      data-testid={`feature-tour-step-${step}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: `${s.color}18` }}
        >
          <Icon className="w-5 h-5" style={{ color: s.color }} />
        </div>
        <button
          onClick={close}
          className="p-1.5 rounded-full hover:bg-zinc-100 transition-colors"
          data-testid="feature-tour-skip"
        >
          <X className="w-4 h-4 text-zinc-400" />
        </button>
      </div>

      <p className="text-[17px] sm:text-lg font-extrabold text-zinc-900 tracking-tight mb-1.5">
        {s.title}
      </p>
      <p className="text-[13px] sm:text-sm text-zinc-600 leading-relaxed mb-3">{s.text}</p>

      {s.bullets && s.bullets.length > 0 && (
        <div className="rounded-2xl bg-zinc-50 p-3 mb-4 space-y-1.5">
          {s.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-2 text-[12.5px] leading-relaxed">
              <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: s.color }} />
              <span className="text-zinc-700">{b}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              className="rounded-full transition-all duration-300"
              style={{ width: i === step ? 18 : 6, height: 6, background: i === step ? s.color : "#E4E4E7" }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {step > 0 && (
            <button
              onClick={prev}
              className="p-2.5 rounded-full bg-zinc-100 hover:bg-zinc-200 transition-colors"
              data-testid="feature-tour-prev"
            >
              <ArrowLeft className="w-4 h-4 text-zinc-600" />
            </button>
          )}
          <button
            onClick={next}
            className="flex items-center gap-1.5 text-white font-bold text-sm rounded-full px-5 py-2.5 transition-transform active:scale-95"
            style={{ background: s.color }}
            data-testid="feature-tour-next"
          >
            {step === steps.length - 1 ? "Ho capito!" : step === 0 ? "Iniziamo" : "Avanti"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[120]"
      data-testid="feature-tour"
      style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}
      ref={scrollRootRef}
    >
      {rect ? (
        <motion.div
          initial={false}
          animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          transition={{ type: "spring", stiffness: 200, damping: 25 }}
          className="fixed rounded-2xl pointer-events-none"
          style={{ boxShadow: "0 0 0 9999px rgba(9,9,11,0.62)", border: `2px solid ${s.color}` }}
        />
      ) : (
        <div className="fixed inset-0" style={{ background: "rgba(9,9,11,0.62)" }} onClick={next} />
      )}

      <AnimatePresence mode="wait">
        {anchored ? card : (
          <div
            key={`wrap-${step}`}
            className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none"
          >
            {card}
          </div>
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default FeatureTour;
